<template>
  <div class="gs-app gv-integrations">
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <v-wait for="integrations">
      <half-circle-spinner
        style="display: block; margin: 0 auto"
        :animation-duration="1000"
        :size="60"
        color="#3d4852"
        slot="waiting"
      />
      <div class="flex flex-col w-full bg-white rounded p-4 gv-integrations__surface">
        <div class="flex flex-col w-1/2 mb-2 gv-integrations__heading">
          <h4 class="font-semibold text-grey-700">
            {{ $t('frontend_groovevideo_videos_integrations') }}
          </h4>
        </div>
        <div class="flex flex-col w-1/2 mb-2 gv-integrations__description">
          <span class="text-sm text-grey-700">
            {{ $t('frontend_groovevideo_videos_integration_configure') }}
          </span>
        </div>
        <div class="flex flex-row mb-2 border-grey-100 border -mx-4 px-4 gv-integrations__tabs">
          <gd-tabs
            :tabs="storageSettingsTabs"
            :currentTab="storageSettingsTab"
            :wrapper-class="'pt-4 px-0 gs-default-tabs'"
            :tab-class="'pb-4 mr-4 gs-default-tabs-item'"
            :tab-active-class="'border-pink border-b-2 text-pink gs-default-tabs-item-active'"
            :line-class="'gs-default-tabs-active-line'"
            @onClick="handleStorageTabChange"
            :key="$t('frontend_groovevideo_videos_integration_storage')"
          />
        </div>
        <div class="mt-2">
          <div
            class="w-full flex flex-col"
            v-if="storageSettingsTab === 'storage'"
          >
            <div class="flex flex-row justify-end w-full gv-integrations__action-row">
              <div class="text-right w-1/5 gv-integrations__action">
                <dropdown-button class="w-full">
                  <span
                    slot="link"
                    class="w-full flex items-center inline-block font-medium pl-3 pr-2 py-1 rounded bg-pink text-white"
                  >
                    <span class="mr-1 pr-2">{{
                      $t('frontend_groovevideo_members_settings_index_add_new')
                    }}</span>
                    <i class="fas fa-angle-down" />
                  </span>
                  <div
                    slot="dropdown"
                    class="bg-white shadow rounded border border-grey-300 overflow-hidden"
                  >
                    <div
                      class="no-underline block p-2 text-grey-darkest bg-white cursor-pointer"
                      v-for="(integration, index) in storageIntegrations"
                      :key="index"
                      @click="addNewIntegration(integration.id)"
                    >
                      {{ integration.name }}
                    </div>
                  </div>
                </dropdown-button>
              </div>
            </div>
            <div class="flex content-start flex-wrap mt-4 gv-integrations__accounts">
              <div
                class="w-full flex flex-row flex-wrap gv-integrations__account-row"
                v-if="storageAccounts.length > 0"
              >
                <div
                  v-for="(storage, storageIndex) in storageAccounts"
                  :key="storageIndex"
                  class="w-1/6 p-2 gv-integrations__account"
                >
                  <div
                    class="w-full h-32 p-4 border border-solid border-grey-200 rounded-lg flex flex-col bg-white text-center gv-integrations__account-card"
                  >
                    <img
                      src="@/assets/images/logo/s3amazon.png"
                      alt="content-img"
                      class="responsive h-16 mx-auto"
                    />
                    <div class="mt-4">
                      {{ storage.name }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                <p>
                  {{ $t('frontend_groovevideo_videos_integration_nostorage') }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="storageSettingsTab === 'encoding'">
            <div class="flex flex-row justify-end w-full gv-integrations__action-row">
              <div class="text-right w-1/5 gv-integrations__action">
                <dropdown-button class="w-full">
                  <span
                    slot="link"
                    class="w-full flex items-center inline-block font-medium pl-3 pr-2 py-1 rounded bg-pink text-white"
                  >
                    <span class="mr-1 pr-2"
                      >+
                      {{
                        $t(
                          'frontend_groovevideo_videos_integration_add_new_encoder'
                        )
                      }}
                    </span>
                    <i class="fas fa-angle-down" />
                  </span>
                  <div
                    slot="dropdown"
                    class="bg-white shadow rounded border border-grey-300 overflow-hidden"
                  >
                    <div
                      class="no-underline block p-2 text-grey-darkest bg-white cursor-pointer"
                      v-for="(integration, index) in encodingIntegration"
                      :key="index"
                      @click="addEncodeIntegration(integration.id)"
                    >
                      {{ integration.name }}
                    </div>
                  </div>
                </dropdown-button>
              </div>
            </div>

            <div class="flex content-start flex-wrap mt-4 gv-integrations__accounts">
              <div
                class="flex flex-row flex-wrap w-full gv-integrations__account-row"
                v-if="encodingAccounts.length > 0"
              >
                <div
                  v-for="(encoding, encodingIndex) in encodingAccounts"
                  :key="encodingIndex"
                  class="w-1/6 p-2 gv-integrations__account"
                >
                  <div
                    class="w-full h-32 p-4 border border-solid border-grey-200 rounded-lg flex flex-col bg-white text-center gv-integrations__account-card"
                  >
                    <img
                      src="@/assets/images/logo/coconut.png"
                      alt="content-img"
                      class="responsive h-16 mx-auto"
                      v-if="encoding.type == 1"
                    />
                    <img
                      src="@/assets/images/logo/Encoding.png"
                      alt="content-img"
                      class="responsive h-16 mx-auto"
                      v-else
                    />
                    <div class="mt-4">
                      {{ encoding.integration_name }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                <p>
                  {{ $t('frontend_groovevideo_videos_integration_no_encoder') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <gs-named-modal
        name="add-s3-account-modal"
        :title="$t('frontend_groovevideo_integration_add_detail')"
      >
        <div class="gs-app gs-refresh-controls flex flex-col m-10 gv-integrations__modal-body">
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">
              {{ $t('frontend_groovevideo_videos_integration_name') }}
            </label>
            <text-input
              :validator="$v.model.integration_name"
              value="model.integration_name"
              v-model="model.integration_name"
              :attribute="$t('frontend_name')"
              :placeholder="$t('frontend_groovevideo_videos_integration_enter')"
            />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">
              {{ $t('frontend_groovevideo_videos_integration_key') }}
            </label>
            <text-input
              :validator="$v.model.key"
              value="model.key"
              v-model="model.key"
              :attribute="$t('frontend_groovevideo_videos_integration_key')"
              :placeholder="
                $t('frontend_groovevideo_videos_integration_enter_key')
              "
            />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">
              {{ $t('frontend_groovevideo_videos_integration_secret') }}
            </label>
            <text-input
              :validator="$v.model.secret"
              value="model.secret"
              v-model="model.secret"
              :attribute="$t('frontend_groovevideo_members_settings_secret')"
              :placeholder="
                $t('frontend_groovevideo_videos_integration_enter_secret_key')
              "
            />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">
              {{ $t('frontend_groovevideo_videos_integration_region') }}
            </label>
            <text-input
              :validator="$v.model.region"
              value="model.region"
              v-model="model.region"
              :attribute="$t('frontend_groovevideo_members_settings_region')"
              :placeholder="
                $t('frontend_groovevideo_videos_integration_enter_region')
              "
            />
          </div>
          <div class="flex flex-row w-full mb-6 justify-end">
            <gs-button
              class="text-center w-40"
              size="lg"
              variant="primary"
              @click="saveStorageDetails()"
            >
              {{ $t('frontend_groovevideo_videos_integration_create') }}
            </gs-button>
          </div>
        </div>
      </gs-named-modal>

      <gs-named-modal
        name="add-coconut-account-modal"
        :title="$t('frontend_groovevideo_integration_add_coconut_details')"
      >
        <div class="gs-app gs-refresh-controls flex flex-col m-10 gv-integrations__modal-body">
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">{{
              $t('frontend_groovevideo_videos_integration_iname')
            }}</label>
            <text-input
              :validator="$v.model.name"
              value="model.name"
              v-model="model.name"
              :attribute="$t('frontend_name')"
              :placeholder="$t('frontend_groovevideo_videos_integration_iname')"
            />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">
              {{ $t('frontend_groovevideo_videos_integratoin_api_key') }}
            </label>
            <text-input
              :validator="$v.model.apikey"
              value="model.apikey"
              v-model="model.apikey"
              :attribute="$t('frontend_groovevideo_videos_integration_key')"
              :placeholder="
                $t('frontend_groovevideo_videos_integratoin_api_key')
              "
            />
          </div>
          <div class="flex flex-row w-full mb-6 justify-end">
            <gs-button
              class="text-center w-40"
              size="lg"
              variant="primary"
              @click="saveCoconutAccount()"
            >
              {{ $t('frontend_groovevideo_videos_integration_create') }}
            </gs-button>
          </div>
        </div>
      </gs-named-modal>

      <gs-named-modal
        name="add-encoding-account-modal"
        :title="$t('frontend_groovevideo_integration_add_detail')"
      >
        <div class="gs-app gs-refresh-controls flex flex-col m-10 gv-integrations__modal-body">
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">{{
              $t('frontend_groovevideo_videos_integration_iname')
            }}</label>
            <text-input
              :validator="$v.model.integration_name"
              value="model.integration_name"
              v-model="model.integration_name"
              :attribute="$t('frontend_name')"
              :placeholder="$t('frontend_groovevideo_videos_integration_enter')"
            />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">
              {{ $t('frontend_groovevideo_videos_enter_integration_user_id') }}
            </label>
            <text-input
              :validator="$v.model.user_encode_id"
              value="model.apikey"
              v-model="model.user_encode_id"
              :attribute="$t('frontend_groovevideo_videos_enter_integration_user_id')"
              :placeholder="
                $t('frontend_groovevideo_videos_enter_integration_user_id')
              "
            />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="font-semibold text-sm text-grey-700 mb-2">
              {{ $t('frontend_groovevideo_videos_enter_integration_user_key') }}
            </label>
            <text-input
              :validator="$v.model.user_encode_key"
              value="model.user_encode_key"
              v-model="model.user_encode_key"
              :attribute="$t('frontend_groovevideo_videos_integration_key')"
              :placeholder="
                $t('frontend_groovevideo_videos_enter_integration_user_key')
              "
            />
          </div>
          <div class="flex flex-row w-full mb-6 justify-end">
            <gs-button
              class="text-center w-40"
              size="lg"
              variant="primary"
              @click="saveEncodingComCredential()"
            >
              {{ $t('frontend_groovevideo_videos_integration_create') }}
            </gs-button>
          </div>
        </div>
      </gs-named-modal>
    </v-wait>
  </div>
</template>

<script>
import Form from 'vform';
import themeConfig from '@/theme/themeConfig.js';
import Loading from 'vue-loading-overlay';
import Storage from '@/models/Storage';
import Swal from 'sweetalert2';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import Encoder from '@/models/Encoder';
import router from '@/router';
import 'vue-loading-overlay/dist/vue-loading.css';
import Notify from '@groovepages/gd-lib/notify';
import Snapshot from '@/assets/images/icons/Focus.svg';
import store from '@/state';

export default {
  middleware: 'auth',
  data() {
    return {
      model: {
        name: '',
        key: '',
        secret: '',
        region: '',
        integration_name: '',
        apikey: '',
        name: '',
        user_encode_id: '',
        user_encode_key: '',
      },
      isLoading: false,
      fullPageLoader: true,
      loaderColor: themeConfig.appPrimaryColor,
      storageSettingsTab: 'storage',
      storageAccounts: [],
      encodingAccounts: [],
      store: {},
      encoder: {},
    };
  },
  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(20),
      },
      key: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(40),
      },
      secret: {
        required,
        minLength: minLength(3),
      },
      region: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(100),
      },
      integration_name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(100),
      },
      apikey: {
        required,
        maxLength: maxLength(100),
      },
      name: {
        required,
        maxLength: maxLength(100),
      },
      user_encode_id: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(100),
      },
      user_encode_key: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(100),
      },
    },
  },
  components: {
    Loading,
  },
  async created() {
    this.$wait.start('integrations');
    this.storageAccounts = await Storage.$get();
    this.encodingAccounts = await Encoder.$get();
    this.$wait.end('integrations');
  },
  methods: {
    handleStorageTabChange(newTab) {
      this.storageSettingsTab = newTab;
    },

    addNewIntegration() {
      this.$root.$emit('modal-open', 'add-s3-account-modal');
    },

    addEncodeIntegration(id) {
      if (id == 1) {
        this.$root.$emit('modal-open', 'add-coconut-account-modal');
      } else if (id == 2) {
        this.$root.$emit('modal-open', 'add-encoding-account-modal');
      }
    },

    async saveStorageDetails() {
      this.$v.model.key.$touch();
      this.$v.model.secret.$touch();
      this.$v.model.region.$touch();
      this.$v.model.integration_name.$touch();

      if (
        !this.$v.model.key.$invalid &&
        !this.$v.model.secret.$invalid &&
        !this.$v.model.region.$invalid &&
        !this.$v.model.integration_name.$invalid
      ) {
        this.showLoader();
        this.store = new Storage({});
        this.store.key = this.model.key;
        this.store.secret_key = this.model.secret;
        this.store.region = this.model.region;
        this.store.integration_name = this.model.integration_name;

        let self = this;

        this.store
          .save()
          .then((response) => {
            if (response.error) {
              Swal.fire(this.$t('frontend_error'), response.error, 'error');
            } else {
              self.store = response.data;
              self.storageAccounts.push(self.store);
            }
            self.$root.$emit('modal-close', 'add-s3-account-modal');
            self.hideLoader();
          })
          .catch((error) => {
            this.hideLoader();
          });
      }
    },

    async saveCoconutAccount() {
      this.$v.model.apikey.$touch();
      if (!this.$v.model.apikey.$invalid) {
        this.showLoader();
        this.encoder = new Encoder({});
        this.encoder.type = 1;
        this.encoder.apikey = this.model.apikey;
        this.encoder.integration_name = this.model.name;

        let self = this;
        this.encoder
          .save()
          .then((response) => {
            self.encoder = response.data;
            self.encodingAccounts.push(self.encoder);
            self.$root.$emit('modal-close', 'add-coconut-account-modal');
            self.hideLoader();
          })
          .catch((error) => {
            self.hideLoader();
          });
      }
    },

    saveEncodingComCredential() {
      this.$v.model.integration_name.$touch();
      this.$v.model.user_encode_id.$touch();
      this.$v.model.user_encode_key.$touch();

      if (
        !this.$v.model.integration_name.$invalid &&
        !this.$v.model.user_encode_id.$invalid &&
        !this.$v.model.user_encode_key.$invalid
      ) {
        this.showLoader();
        this.encoder = new Encoder({});
        this.encoder.type = 2;
        this.encoder.integration_name = this.model.integration_name;
        this.encoder.user_encode_id = this.model.user_encode_id;
        this.encoder.user_encode_key = this.model.user_encode_key;

        let self = this;

        this.encoder
          .save()
          .then((response) => {
            if (response.error) {
              Swal.fire(this.$t('frontend_error'), response.error, 'error');
            } else {
              self.encoder = response.data;
              self.encodingAccounts.push(self.encoder);
            }

            self.$root.$emit('modal-close', 'add-encoding-account-modal');
            self.hideLoader();
          })
          .catch((error) => {
            self.hideLoader();
          });
      }
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
  },
  mounted() {},
  computed: {
    storageSettingsTabs() {
      const storageSettingsTabs = [
        {
          title: this.$t('frontend_groovevideo_videos_integration_storage'),
          value: 'storage',
        },
        {
          title: this.$t('frontend_groovevideo_videos_integration_encoding'),
          value: 'encoding',
        },
      ];
      return storageSettingsTabs;
    },
    storageIntegrations() {
      const storageIntegrations = [
        { name: this.$t('frontend_groovevideo_add_new_s3_integration') },
      ];
      return storageIntegrations;
    },
    encodingIntegration() {
      const encodingIntegration = [
        { name: this.$t('frontend_groovevideo_add_coconut_encoding'), id: 1 },
        {
          name: this.$t('frontend_groovevideo_add_encoding_integration'),
          id: 2,
        },
      ];

      return encodingIntegration;
    },
  },
};
</script>

<style lang="scss">
.gv-integrations__surface {
  gap: 8px;
  padding: 24px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gv-integrations__tabs {
  margin-top: 12px;
}

.gv-integrations__accounts {
  margin-left: -8px;
  margin-right: -8px;
}

.gv-integrations__account-card {
  height: auto;
  min-height: 8rem;
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gv-integrations__modal-body {
  gap: 8px;
  max-width: 42rem;
}

@media (max-width: 960px) {
  .gv-integrations__account {
    width: 33.333333% !important;
  }
}

@media (max-width: 640px) {
  .gv-integrations__surface {
    padding: 16px !important;
  }

  .gv-integrations__heading,
  .gv-integrations__description,
  .gv-integrations__action {
    width: 100% !important;
  }

  .gv-integrations__action-row {
    justify-content: stretch;
  }

  .gv-integrations__account {
    width: 100% !important;
  }

  .gv-integrations__modal-body {
    margin: 16px !important;
  }
}
</style>

<style scoped>
.gv-integrations__account-row {
  flex-wrap: wrap;
}

@media (max-width: 640px) {
  .gv-integrations__account-row {
    flex-direction: column;
  }
}
</style>
